/**
 * A line's event log, and the two dialogs that write to it: add an event and
 * void one. On the line's own page.
 *
 * THE LOG IS SHOWN IN THE API'S ORDER (fieldiq-api orderLineEvents: occurred,
 * then created, then id) and never re-sorted here. A voided event stays in the
 * log, struck through, with who voided it and why: a correction is a void with
 * a reason, then a new event (the API has no edit).
 *
 * Add event is WL_GEAR_WRITE (admin, office_staff, supervisor); void is
 * WL_ADMIN (admin, office_staff). Each refusal reads as the API's sentence in
 * the dialog — exceeds_length, not_spooled, already_spooled, after_retired,
 * already_voided — never as a green toast.
 *
 * The time is typed on the TENANT's clock (meta.timeZone) and sent as an
 * instant. A time later than now is a WARNING: said, never blocking.
 */
import React, { useMemo, useState } from 'react';
import { Ban } from 'lucide-react';
import { toast } from 'sonner';
import { useAddWlLineEvent, useVoidWlLineEvent, useWlEquipmentOptions } from '../../hooks/useWirelineSlickline';
import { formatInstant, zonedParts, zonedWallClockToInstant } from '../../lib/calendar-date';
import { WL_EVENT_FIELDS, WL_VOIDED_SKIN, wlCatalogLabel, wlRefusal, wlWithUnit } from '../../lib/wireline-slickline';
import type { WlCatalog, WlLineEvent, WlLineEventWrite, WlMeta } from '../../types/wireline-slickline';
import { WL_INPUT, WL_PRIMARY, WL_SECONDARY, WL_SMALL_BTN, WlBadge, WlDialog, WlEmpty, WlField, WlFormError, WlPanel, WlWarning } from './WlBits';

const pad = (n: number) => String(n).padStart(2, '0');

/** A plain positive number as typed (no thousands separator): what the API reads as a figure. */
export const wlIsPositiveNumber = (v: string) => /^\d+(\.\d+)?$/.test(v.trim()) && Number(v.trim()) > 0;

/** The tenant's wall clock now, as a datetime-local value; '' when the zone is unknown. */
export function tenantNowInput(timeZone: string | null | undefined, now: Date = new Date()): string {
  const p = zonedParts(now, timeZone);
  return p ? `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}` : '';
}

/** A datetime-local value read on the tenant's clock, as an ISO instant; null when it cannot be. */
export function tenantInputToIso(value: string, timeZone: string | null | undefined): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (!m) return null;
  const at = zonedWallClockToInstant(Number(m[1]), Number(m[2]), Number(m[3]), Number(m[4]), Number(m[5]), timeZone);
  return at ? at.toISOString() : null;
}

/** What the event recorded, in its entered units; nothing derived. */
function eventDetail(e: WlLineEvent): string[] {
  const out: string[] = [];
  if (e.length != null) out.push(`Length ${wlWithUnit(e.length, e.lengthUnit)}`);
  if (e.drumEquipmentId) out.push(`Drum ${e.drumName || '—'}${e.drumUnitNumber ? ` · ${e.drumUnitNumber}` : ''}`);
  if (e.load != null) out.push(`Load ${wlWithUnit(e.load, e.loadUnit)}`);
  if (e.result) out.push(`Result ${e.resultLabel ?? e.result}`);
  return out;
}

export function LineEventLog({
  events,
  catalog,
  meta,
  canVoid,
  onVoid,
}: {
  events: WlLineEvent[];
  catalog: WlCatalog | null;
  meta: WlMeta | null;
  canVoid: boolean;
  onVoid: (e: WlLineEvent) => void;
}) {
  const zone = meta?.timeZone ?? null;
  return (
    <WlPanel eyebrow="Event log" title="Events" testId="wl-line-events">
      {events.length === 0 ? (
        <WlEmpty>No events yet. Spooling the line onto a drum is its first.</WlEmpty>
      ) : (
        <ol className="divide-y divide-fieldiq-border">
          {events.map((e) => (
            <li key={e.id} className="flex flex-wrap items-start justify-between gap-3 px-4 py-3" data-testid="wl-line-event" data-voided={e.voided ? 'true' : 'false'}>
              <div className={`min-w-0 ${e.voided ? 'opacity-70' : ''}`}>
                <p className={`font-extrabold text-white ${e.voided ? 'line-through' : ''}`}>
                  {e.eventTypeLabel ?? wlCatalogLabel(catalog?.lineEventTypes, e.eventType)}
                  <span className="ml-2 text-xs font-normal text-fieldiq-muted">{formatInstant(e.occurredAt, zone) ?? '—'}</span>
                </p>
                {eventDetail(e).length > 0 && <p className={`mt-0.5 font-mono text-xs text-fieldiq-text ${e.voided ? 'line-through' : ''}`}>{eventDetail(e).join(' · ')}</p>}
                {e.notes && <p className="mt-1 text-sm text-fieldiq-text">{e.notes}</p>}
                <p className="mt-1 text-xs text-fieldiq-muted">Logged by {e.createdByName || '—'}</p>
                {e.voided && (
                  <p className="mt-1 text-xs text-fieldiq-text" data-testid="wl-line-event-void-reason">
                    <WlBadge skin={WL_VOIDED_SKIN} value="voided" /> <span className="ml-1">by {e.voidedByName || '—'}: {e.voidReason || '—'}</span>
                  </p>
                )}
              </div>
              {canVoid && !e.voided && (
                <button type="button" className={WL_SMALL_BTN} onClick={() => onVoid(e)} aria-label={`Void ${e.eventTypeLabel ?? e.eventType}`}>
                  <Ban className="size-3.5" aria-hidden="true" />
                  Void
                </button>
              )}
            </li>
          ))}
        </ol>
      )}
    </WlPanel>
  );
}

export function AddEventDialog({
  lineId,
  catalog,
  meta,
  onClose,
  now = () => new Date(),
}: {
  lineId: string;
  catalog: WlCatalog | null;
  meta: WlMeta | null;
  onClose: () => void;
  /** The clock the future-time warning reads; a test pins it. */
  now?: () => Date;
}) {
  const zone = meta?.timeZone ?? null;
  const add = useAddWlLineEvent();
  const equipment = useWlEquipmentOptions();
  const [type, setType] = useState('');
  const [at, setAt] = useState(() => tenantNowInput(zone, now()));
  const [length, setLength] = useState('');
  const [lengthUnit, setLengthUnit] = useState(meta?.units?.length ?? '');
  const [drum, setDrum] = useState('');
  const [load, setLoad] = useState('');
  const [loadUnit, setLoadUnit] = useState(meta?.units?.force ?? '');
  const [result, setResult] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const fields = WL_EVENT_FIELDS[type] ?? { length: null, drum: null, load: false, result: null };
  const lengthUnits = catalog?.lengthUnits?.length ? catalog.lengthUnits : lengthUnit ? [lengthUnit] : [];
  const forceUnits = catalog?.forceUnits?.length ? catalog.forceUnits : loadUnit ? [loadUnit] : [];

  const iso = zone ? tenantInputToIso(at, zone) : null;
  const warning = useMemo(() => {
    if (!iso) return null;
    return Date.parse(iso) > now().getTime() ? 'This time is later than now. Check it before saving; the event will be logged as typed.' : null;
  }, [iso, now]);

  const save = () => {
    setError(null);
    if (!type) return setError('Pick what happened to the line.');
    if (!zone) return setError('Your company time zone did not load, so the time cannot be read. Reload and try again.');
    if (!iso) return setError('Enter when it happened.');
    // A figure that is not a plain number would reach the API as null and be dropped silently.
    if (fields.length && length.trim() !== '' && !wlIsPositiveNumber(length)) return setError('Length must be a number greater than 0, e.g. 1200.');
    if (fields.load && load.trim() !== '' && !wlIsPositiveNumber(load)) return setError('Load must be a number greater than 0, e.g. 1200.');
    const body: WlLineEventWrite = { event_type: type, occurred_at: iso };
    if (fields.length && length.trim() !== '') {
      body.length = Number(length.trim());
      body.length_unit = lengthUnit || null;
    }
    if (fields.drum && drum) body.drum_equipment_id = drum;
    if (fields.load && load.trim() !== '') {
      body.load = Number(load.trim());
      body.load_unit = loadUnit || null;
    }
    if (fields.result && result) body.result = result;
    if (notes.trim()) body.notes = notes.trim();
    add.mutate(
      { lineId, body },
      {
        onSuccess: () => {
          toast.success('Event logged');
          onClose();
        },
        onError: (err) => setError(wlRefusal(err, 'The event could not be logged.')),
      },
    );
  };

  return (
    <WlDialog
      open
      wide
      title="Log a line event"
      onClose={onClose}
      footer={
        <>
          <button type="button" className={WL_SECONDARY} onClick={onClose} disabled={add.isPending}>
            Cancel
          </button>
          <button type="button" className={WL_PRIMARY} onClick={save} disabled={add.isPending}>
            {add.isPending ? 'Saving…' : 'Log event'}
          </button>
        </>
      }
    >
      <div className="space-y-3" data-testid="wl-add-event-dialog">
        <WlFormError message={error} />
        <div className="grid gap-3 sm:grid-cols-2">
          <WlField label="Event">
            <select className={WL_INPUT} value={type} onChange={(e) => setType(e.target.value)} aria-label="Event">
              <option value="">Select an event…</option>
              {(catalog?.lineEventTypes ?? []).map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </WlField>
          <WlField label="When" hint="Your company's clock.">
            <input type="datetime-local" className={WL_INPUT} value={at} onChange={(e) => setAt(e.target.value)} aria-label="When" />
          </WlField>
          {fields.length && (
            <WlField label={fields.length === 'required' ? 'Length' : 'Length (optional)'}>
              <div className="flex gap-2">
                <input className={WL_INPUT} inputMode="decimal" value={length} onChange={(e) => setLength(e.target.value)} aria-label="Length" />
                <select className={`${WL_INPUT} w-20 shrink-0`} value={lengthUnit} onChange={(e) => setLengthUnit(e.target.value)} aria-label="Length unit">
                  {!lengthUnit && <option value="">—</option>}
                  {lengthUnits.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </div>
            </WlField>
          )}
          {fields.drum && (
            <WlField label={fields.drum === 'required' ? 'Drum' : 'Drum (optional)'} hint="The drum's unit in Equipment.">
              <select className={WL_INPUT} value={drum} onChange={(e) => setDrum(e.target.value)} aria-label="Drum">
                <option value="">{equipment.isLoading ? 'Loading…' : 'Select a drum…'}</option>
                {(equipment.data ?? []).map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                    {x.unitNumber ? ` · ${x.unitNumber}` : ''}
                  </option>
                ))}
              </select>
            </WlField>
          )}
          {fields.load && (
            <WlField label="Load">
              <div className="flex gap-2">
                <input className={WL_INPUT} inputMode="decimal" value={load} onChange={(e) => setLoad(e.target.value)} aria-label="Load" />
                <select className={`${WL_INPUT} w-24 shrink-0`} value={loadUnit} onChange={(e) => setLoadUnit(e.target.value)} aria-label="Load unit">
                  {!loadUnit && <option value="">—</option>}
                  {forceUnits.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </div>
            </WlField>
          )}
          {fields.result && (
            <WlField label={fields.result === 'required' ? 'Result' : 'Result (optional)'}>
              <select className={WL_INPUT} value={result} onChange={(e) => setResult(e.target.value)} aria-label="Result">
                <option value="">{fields.result === 'required' ? 'Select a result…' : 'Not recorded'}</option>
                {(catalog?.testResults ?? []).map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </WlField>
          )}
        </div>
        <WlField label="Notes">
          <textarea className={`${WL_INPUT} h-20 py-2`} value={notes} onChange={(e) => setNotes(e.target.value)} aria-label="Event notes" />
        </WlField>
        <WlWarning message={warning} testId="wl-event-warning" />
      </div>
    </WlDialog>
  );
}

export function VoidEventDialog({ event, catalog, onClose }: { event: WlLineEvent; catalog: WlCatalog | null; onClose: () => void }) {
  const voidEvent = useVoidWlLineEvent();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const label = event.eventTypeLabel ?? wlCatalogLabel(catalog?.lineEventTypes, event.eventType);

  const confirm = () => {
    setError(null);
    if (!reason.trim()) return setError('Say why this event is being voided.');
    voidEvent.mutate(
      { eventId: event.id, reason: reason.trim() },
      {
        onSuccess: () => {
          toast.success('Event voided');
          onClose();
        },
        onError: (err) => setError(wlRefusal(err, 'The event could not be voided.')),
      },
    );
  };

  return (
    <WlDialog
      open
      title={`Void ${label}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={WL_SECONDARY} onClick={onClose} disabled={voidEvent.isPending}>
            Keep event
          </button>
          <button type="button" className={WL_PRIMARY} onClick={confirm} disabled={voidEvent.isPending}>
            {voidEvent.isPending ? 'Voiding…' : 'Void event'}
          </button>
        </>
      }
    >
      <div className="space-y-3" data-testid="wl-void-dialog">
        <WlFormError message={error} />
        <p className="text-sm text-fieldiq-text">
          The event stays in the log, struck through, and stops counting toward the line's state. To correct it, void it and log the right one.
        </p>
        <WlField label="Reason">
          <textarea className={`${WL_INPUT} h-20 py-2`} maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} aria-label="Reason" />
        </WlField>
      </div>
    </WlDialog>
  );
}
